<template>
  <div class="overflow-x-auto">
    <table class="min-w-full">
      <thead>
        <tr class="border-b border-border bg-gray-50/60">
          <th class="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
          <th class="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
          <th class="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
          <th class="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-border">
        <tr v-for="payment in payments" :key="payment.id" class="hover:bg-gray-50/50 transition-colors">
          <td class="px-6 py-3.5 whitespace-nowrap text-sm text-gray-900">
            {{ formatDate(payment.payment_date) }}
          </td>
          <td class="px-6 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900">
            {{ formatAmount(payment) }}
          </td>
          <td class="px-6 py-3.5 whitespace-nowrap">
            <span
              class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
              :class="getPaymentStatusClasses(payment.status)"
            >
              <span class="w-1.5 h-1.5 rounded-full" :class="getPaymentStatusDotClass(payment.status)"></span>
              {{ getPaymentStatusLabel(payment.status) }}
            </span>
          </td>
          <td class="px-6 py-3.5 whitespace-nowrap text-sm text-muted-foreground">
            {{ payment.subscription_plan_name || 'Subscription Payment' }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS
} from '@/types/subscription'
import type { SubscriptionPayment } from '@/types/subscription'

interface Props {
  payments: SubscriptionPayment[]
}

defineProps<Props>()

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })

const formatAmount = (payment: SubscriptionPayment) => {
  if (payment.formatted_amount) {
    return payment.currency.toUpperCase() === payment.formatted_amount.slice(0, 3)
      ? payment.formatted_amount
      : `${payment.formatted_amount} ${payment.currency.toUpperCase()}`
  }

  const normalizedAmount = Number(payment.amount)
  if (Number.isFinite(normalizedAmount)) {
    return `${payment.currency.toUpperCase()} ${(normalizedAmount / 100).toFixed(2)}`
  }

  return payment.currency.toUpperCase()
}

const getPaymentStatusLabel = (status: SubscriptionPayment['status']) =>
  PAYMENT_STATUS_LABELS[status] ?? status

const getPaymentStatusClasses = (status: SubscriptionPayment['status']) => {
  const color = PAYMENT_STATUS_COLORS[status] ?? 'gray'

  const colorMap: Record<string, string> = {
    green: 'bg-green-50 text-green-700 ring-1 ring-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200',
    red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    gray: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
  }

  return colorMap[color] ?? colorMap.gray
}

const getPaymentStatusDotClass = (status: SubscriptionPayment['status']) => {
  const color = PAYMENT_STATUS_COLORS[status] ?? 'gray'

  const dotMap: Record<string, string> = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    gray: 'bg-gray-400'
  }

  return dotMap[color] ?? dotMap.gray
}
</script>
